
import React, { useEffect, useState } from 'react';
import { api } from '../services/api';
import { Order, FileUploadProgress } from '../types';
import { Upload, CheckCircle, AlertCircle, File, Loader2, Phone } from 'lucide-react';
import { Button, Card, Input } from './ui';

interface PublicUploadViewProps {
  token: string;
}

export const PublicUploadView = ({ token }: PublicUploadViewProps) => {
  const [order, setOrder] = useState<Order | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [uploads, setUploads] = useState<FileUploadProgress[]>([]);
  const [phone, setPhone] = useState('');

  useEffect(() => {
    api.getOrderByToken(token)
      .then(data => {
         if (data) setOrder(data);
         else setError('Invalid upload link or order not found.');
      })
      .catch(() => setError('Failed to verify link.'))
      .finally(() => setLoading(false));
  }, [token]);

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files || !order) return;
    const files: File[] = Array.from(e.target.files);
    
    // Create progress entries
    const newUploads = files.map(f => ({
      id: Math.random().toString(36).substr(2, 9),
      fileName: f.name,
      progress: 0,
      status: 'uploading' as const
    }));
    setUploads(prev => [...prev, ...newUploads]);

    let successCount = 0;

    // Process uploads
    for (let i = 0; i < files.length; i++) {
       const file = files[i];
       const uploadId = newUploads[i].id;
       
       try {
          // Update progress to 50%
          setUploads(curr => curr.map(u => u.id === uploadId ? { ...u, progress: 50 } : u));
          
          await api.uploadFile(order.id, file);
          successCount++;
          
          // Complete
          setUploads(curr => curr.map(u => u.id === uploadId ? { ...u, progress: 100, status: 'completed' } : u));
       } catch (err) {
          console.error(err);
          setUploads(curr => curr.map(u => u.id === uploadId ? { ...u, status: 'error' } : u));
       }
    }

    if (successCount > 0) {
        // Pass the manually entered phone number to the notification system
        api.notifyFileUpload(order.id, phone ? `Public Uploader (${phone})` : 'Public Uploader', successCount, phone);
    }
  };

  if (loading) return <div className="min-h-screen bg-black flex items-center justify-center text-white">Verifying link...</div>;
  if (error || !order) return <div className="min-h-screen bg-black flex items-center justify-center text-red-400">{error}</div>;

  return (
    <div className="min-h-screen bg-black text-zinc-100 p-6 flex flex-col items-center justify-center">
       <div className="max-w-md w-full space-y-8">
          <div className="text-center">
             <div className="w-16 h-16 bg-white/10 rounded-2xl flex items-center justify-center mx-auto mb-6">
                <Upload size={32} className="text-blue-400" />
             </div>
             <h1 className="text-2xl font-bold text-white mb-2">Upload Files</h1>
             <p className="text-zinc-400">Request for Patient: <span className="text-white font-medium">{order.patientName}</span></p>
             <p className="text-xs text-zinc-600 mt-1">Order #{order.toothNumber || order.id.slice(0,6)}</p>
          </div>

          <Card className="bg-zinc-900 border-zinc-800 p-8">
             {/* Optional Phone Input for anonymous uploaders */}
             <div className="mb-6 relative">
                <Input 
                   label="Your WhatsApp (Optional)"
                   placeholder="+20 1xxxxxxxxx"
                   value={phone}
                   onChange={(e: any) => setPhone(e.target.value)}
                   className="pl-10"
                />
                <Phone className="absolute left-3 top-[38px] text-zinc-500" size={16}/>
                <p className="text-[10px] text-zinc-500 mt-1">Enter your number to receive a confirmation receipt.</p>
             </div>

             <div className="border-2 border-dashed border-zinc-700 rounded-xl p-8 text-center hover:bg-zinc-800/50 transition-colors relative group">
                <input 
                  type="file" 
                  multiple 
                  className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                  onChange={handleFileUpload}
                />
                <div className="pointer-events-none">
                   <div className="w-12 h-12 bg-blue-500/10 rounded-full flex items-center justify-center mx-auto mb-4 text-blue-400 group-hover:scale-110 transition-transform">
                      <File size={24} />
                   </div>
                   <p className="font-medium text-white mb-1">Click to browse</p>
                   <p className="text-xs text-zinc-500">STL, OBJ, PLY, JPG, PNG</p>
                </div>
             </div>

             {uploads.length > 0 && (
                <div className="mt-6 space-y-3">
                   <h4 className="text-xs font-bold text-zinc-500 uppercase">Upload Queue</h4>
                   <div className="max-h-60 overflow-y-auto space-y-2 pr-2 custom-scrollbar">
                      {uploads.map(u => (
                         <div key={u.id} className="flex items-center gap-3 bg-black/30 p-2.5 rounded-lg border border-white/5">
                            {u.status === 'uploading' && <Loader2 size={16} className="animate-spin text-blue-400"/>}
                            {u.status === 'completed' && <CheckCircle size={16} className="text-green-400"/>}
                            {u.status === 'error' && <AlertCircle size={16} className="text-red-400"/>}
                            <span className="text-sm text-zinc-300 truncate flex-1">{u.fileName}</span>
                            {u.status === 'uploading' && <span className="text-xs text-zinc-500">{u.progress}%</span>}
                         </div>
                      ))}
                   </div>
                </div>
             )}
          </Card>
          
          <div className="text-center">
             <p className="text-zinc-600 text-xs">Files are securely uploaded to Galal Dental Lab.</p>
          </div>
       </div>
    </div>
  );
};
